import React, { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { useWebsocketContext } from '../Websocket/WebsocketProvider'
import { useActiveUser } from '../../api/slack/useActiveUser'

// Notification sound - a short, subtle chime (generated programmatically)
let notificationAudioContext: AudioContext | null = null

const createNotificationSound = (): AudioContext | null => {
  try {
    if (!notificationAudioContext) {
      notificationAudioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
    }
    return notificationAudioContext
  } catch {
    return null
  }
}

// Browser tab notification - flash title and show unread count
const originalTitle = document.title
let titleFlashInterval: NodeJS.Timeout | null = null

const flashTabTitle = (senderName: string) => {
  // Clear any existing flash
  if (titleFlashInterval) {
    clearInterval(titleFlashInterval)
    titleFlashInterval = null
  }

  let isFlashing = true
  let flashCount = 0
  const maxFlashes = 6 // 3 complete cycles

  titleFlashInterval = setInterval(() => {
    if (flashCount >= maxFlashes) {
      // Stop flashing, title will be updated by the unread count effect
      if (titleFlashInterval) {
        clearInterval(titleFlashInterval)
        titleFlashInterval = null
      }
      return
    }

    document.title = isFlashing ? `💬 ${senderName}` : originalTitle
    isFlashing = !isFlashing
    flashCount++
  }, 500)
}

const updateTitleWithUnreadCount = (count: number) => {
  // Don't update if currently flashing
  if (titleFlashInterval) return

  if (count > 0) {
    document.title = `(${count}) ${originalTitle}`
  } else {
    document.title = originalTitle
  }
}

export interface QuickMessage {
  id: string
  fromUserId: string
  toUserId: string
  senderName: string
  senderImage?: string
  message: string
  timestamp: number
  read: boolean
  delivered: boolean
}

export interface Conversation {
  odUserId: string
  userName: string
  userImage?: string
  messages: QuickMessage[]
  unreadCount: number
  lastMessageTime: number
  isTyping: boolean
}

interface QuickChatContextValue {
  conversations: Map<string, Conversation>
  totalUnreadCount: number
  sendMessage: (targetUserId: string, message: string) => void
  markAsRead: (userId: string) => void
  isDrawerOpen: boolean
  setDrawerOpen: (open: boolean) => void
  activeConversation: string | null
  setActiveConversation: (userId: string | null) => void
  sendTypingIndicator: (targetUserId: string, isTyping: boolean) => void
  sendTestMessage: (message: string) => void // Test mode: send message to yourself
}

const QuickChatContext = createContext<QuickChatContextValue | undefined>(undefined)

// Get today's date key for localStorage
const getTodayKey = () => {
  const today = new Date()
  return `quickChat_${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
}

// Load messages from localStorage (only today's messages)
const loadMessages = (): Map<string, Conversation> => {
  const todayKey = getTodayKey()
  try {
    const stored = localStorage.getItem(todayKey)
    if (stored) {
      const parsed = JSON.parse(stored)
      return new Map(Object.entries(parsed))
    }
  } catch (e) {
    console.error('[QuickChat] Failed to load from localStorage:', e)
  }
  return new Map()
}

// Save messages to localStorage
const saveMessages = (conversations: Map<string, Conversation>) => {
  const todayKey = getTodayKey()
  try {
    // Clean up old keys (keep only today's)
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key?.startsWith('quickChat_') && key !== todayKey) {
        localStorage.removeItem(key)
      }
    }
    // Save current conversations
    const obj = Object.fromEntries(conversations)
    localStorage.setItem(todayKey, JSON.stringify(obj))
  } catch (e) {
    console.error('[QuickChat] Failed to save to localStorage:', e)
  }
}

// Play notification sound using Web Audio API
const playNotificationSound = () => {
  try {
    const audioCtx = createNotificationSound()
    if (!audioCtx) return

    // Resume context if suspended (browser autoplay policy)
    if (audioCtx.state === 'suspended') {
      audioCtx.resume()
    }

    const oscillator = audioCtx.createOscillator()
    const gainNode = audioCtx.createGain()

    oscillator.connect(gainNode)
    gainNode.connect(audioCtx.destination)

    // Minimal Blip - extremely brief and subtle
    // Alternative: Double Tap (see SoundTester.tsx for implementation if needed)
    oscillator.frequency.setValueAtTime(440, audioCtx.currentTime)
    oscillator.type = 'sine'

    gainNode.gain.setValueAtTime(0.05, audioCtx.currentTime)
    gainNode.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.05)

    oscillator.start(audioCtx.currentTime)
    oscillator.stop(audioCtx.currentTime + 0.05)
  } catch {
    // Ignore audio errors
  }
}

export const QuickChatProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { ws, connectedUsers } = useWebsocketContext()
  const { activeUser } = useActiveUser()
  const [conversations, setConversations] = useState<Map<string, Conversation>>(() => loadMessages())
  const [isDrawerOpen, setDrawerOpen] = useState(false)
  const [activeConversation, setActiveConversation] = useState<string | null>(null)
  const wsListenerRef = useRef<((event: MessageEvent) => void) | null>(null)

  const currentUserId = activeUser?.user?.id ?? ''
  const currentUserName = activeUser?.user?.real_name ?? ''
  const currentUserImage = activeUser?.user?.profile?.image_512 ?? ''

  // Calculate total unread count
  const totalUnreadCount = useMemo(() => {
    let count = 0
    conversations.forEach(conv => {
      count += conv.unreadCount
    })
    return count
  }, [conversations])

  // Save to localStorage whenever conversations change
  useEffect(() => {
    saveMessages(conversations)
  }, [conversations])

  // Update browser tab title with unread count
  useEffect(() => {
    updateTitleWithUnreadCount(totalUnreadCount)

    // Cleanup: restore original title on unmount
    return () => {
      if (titleFlashInterval) {
        clearInterval(titleFlashInterval)
        titleFlashInterval = null
      }
      document.title = originalTitle
    }
  }, [totalUnreadCount])

  // Handle incoming WebSocket messages
  useEffect(() => {
    if (!ws) return

    const handleMessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data)

        switch (data.type) {
          case 'direct_message': {
            const { messageId, fromUserId, senderName, senderImage, message, timestamp } = data

            // Play notification sound and flash tab title
            playNotificationSound()
            flashTabTitle(senderName)

            setConversations(prev => {
              const newConversations = new Map(prev)
              const existing = newConversations.get(fromUserId) || {
                odUserId: fromUserId,
                userName: senderName,
                userImage: senderImage,
                messages: [],
                unreadCount: 0,
                lastMessageTime: 0,
                isTyping: false,
              }

              const newMessage: QuickMessage = {
                id: messageId,
                fromUserId,
                toUserId: currentUserId,
                senderName,
                senderImage,
                message,
                timestamp,
                read: false,
                delivered: true,
              }

              existing.messages.push(newMessage)
              existing.unreadCount += 1
              existing.lastMessageTime = timestamp
              existing.isTyping = false

              newConversations.set(fromUserId, existing)
              return newConversations
            })
            break
          }

          case 'message_sent': {
            const { messageId, delivered } = data
            setConversations(prev => {
              const newConversations = new Map(prev)
              newConversations.forEach(conv => {
                const msgIndex = conv.messages.findIndex(m => m.id === messageId)
                if (msgIndex !== -1) {
                  conv.messages[msgIndex].delivered = delivered
                }
              })
              return newConversations
            })
            break
          }

          case 'message_read_receipt': {
            const { messageId } = data
            setConversations(prev => {
              const newConversations = new Map(prev)
              newConversations.forEach(conv => {
                const msgIndex = conv.messages.findIndex(m => m.id === messageId)
                if (msgIndex !== -1) {
                  conv.messages[msgIndex].read = true
                }
              })
              return newConversations
            })
            break
          }

          case 'typing_indicator': {
            const { fromUserId, isTyping } = data
            setConversations(prev => {
              const newConversations = new Map(prev)
              const existing = newConversations.get(fromUserId)
              if (existing) {
                existing.isTyping = isTyping
                newConversations.set(fromUserId, existing)
              }
              return newConversations
            })
            break
          }
        }
      } catch (e) {
        // Ignore parse errors for other message types
      }
    }

    // Remove old listener if exists
    if (wsListenerRef.current) {
      ws.removeEventListener('message', wsListenerRef.current)
    }

    ws.addEventListener('message', handleMessage)
    wsListenerRef.current = handleMessage

    return () => {
      ws.removeEventListener('message', handleMessage)
      wsListenerRef.current = null
    }
  }, [ws, currentUserId])

  // Send a message
  const sendMessage = useCallback((targetUserId: string, message: string) => {
    if (!ws || ws.readyState !== WebSocket.OPEN || !message.trim()) return

    const messageId = `${Date.now()}-${Math.random().toString(36).slice(2)}`
    const timestamp = Date.now()

    // Find target user info from connected users
    const targetUser = connectedUsers.find(u => u.id === targetUserId)
    const targetUserName = targetUser?.name ?? targetUser?.firstName ?? 'Unknown'

    // Send via WebSocket
    ws.send(JSON.stringify({
      type: 'direct_message',
      targetUserId,
      message: message.trim(),
      senderName: currentUserName,
      senderImage: currentUserImage,
    }))

    // Add to local conversations immediately (optimistic update)
    setConversations(prev => {
      const newConversations = new Map(prev)
      const existing = newConversations.get(targetUserId) || {
        odUserId: targetUserId,
        userName: targetUserName,
        userImage: targetUser?.image_512,
        messages: [],
        unreadCount: 0,
        lastMessageTime: 0,
        isTyping: false,
      }

      const newMessage: QuickMessage = {
        id: messageId,
        fromUserId: currentUserId,
        toUserId: targetUserId,
        senderName: currentUserName,
        senderImage: currentUserImage,
        message: message.trim(),
        timestamp,
        read: false,
        delivered: false, // Will be updated via message_sent response
      }

      existing.messages.push(newMessage)
      existing.lastMessageTime = timestamp

      newConversations.set(targetUserId, existing)
      return newConversations
    })
  }, [ws, connectedUsers, currentUserId, currentUserName, currentUserImage])

  // Mark messages as read
  const markAsRead = useCallback((userId: string) => {
    setConversations(prev => {
      const newConversations = new Map(prev)
      const existing = newConversations.get(userId)
      if (existing && existing.unreadCount > 0) {
        // Send read receipts for unread messages
        existing.messages.forEach(msg => {
          if (!msg.read && msg.fromUserId === userId) {
            msg.read = true
            // Notify sender
            if (ws && ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({
                type: 'message_read',
                messageId: msg.id,
                fromUserId: msg.fromUserId,
              }))
            }
          }
        })
        existing.unreadCount = 0
        newConversations.set(userId, existing)
      }
      return newConversations
    })
  }, [ws])

  // Send typing indicator
  const sendTypingIndicator = useCallback((targetUserId: string, isTyping: boolean) => {
    if (!ws || ws.readyState !== WebSocket.OPEN) return

    ws.send(JSON.stringify({
      type: 'typing_indicator',
      targetUserId,
      isTyping,
      senderName: currentUserName,
    }))
  }, [ws, currentUserName])

  // Test mode: send message to yourself to test the UI
  const sendTestMessage = useCallback((message: string) => {
    if (!message.trim()) return

    const messageId = `test-${Date.now()}-${Math.random().toString(36).slice(2)}`
    const timestamp = Date.now()
    const testUserId = '__test_user__'

    // Add outgoing message
    setConversations(prev => {
      const newConversations = new Map(prev)
      const existing = newConversations.get(testUserId) || {
        odUserId: testUserId,
        userName: 'Test Mode (You)',
        userImage: currentUserImage,
        messages: [],
        unreadCount: 0,
        lastMessageTime: 0,
        isTyping: false,
      }

      const newMessage: QuickMessage = {
        id: messageId,
        fromUserId: currentUserId,
        toUserId: testUserId,
        senderName: currentUserName,
        senderImage: currentUserImage,
        message: message.trim(),
        timestamp,
        read: false,
        delivered: true,
      }

      existing.messages.push(newMessage)
      existing.lastMessageTime = timestamp
      newConversations.set(testUserId, existing)
      return newConversations
    })

    // Simulate receiving an echo reply after 1 second
    setTimeout(() => {
      playNotificationSound()
      flashTabTitle('Test Mode')
      const replyId = `test-reply-${Date.now()}-${Math.random().toString(36).slice(2)}`
      const replyTimestamp = Date.now()

      setConversations(prev => {
        const newConversations = new Map(prev)
        const existing = newConversations.get(testUserId)
        if (existing) {
          const replyMessage: QuickMessage = {
            id: replyId,
            fromUserId: testUserId,
            toUserId: currentUserId,
            senderName: 'Test Mode (Echo)',
            senderImage: currentUserImage,
            message: `Echo: ${message.trim()}`,
            timestamp: replyTimestamp,
            read: false,
            delivered: true,
          }
          existing.messages.push(replyMessage)
          existing.unreadCount += 1
          existing.lastMessageTime = replyTimestamp
          newConversations.set(testUserId, existing)
        }
        return newConversations
      })
    }, 1000)
  }, [currentUserId, currentUserName, currentUserImage])

  const value = useMemo<QuickChatContextValue>(() => ({
    conversations,
    totalUnreadCount,
    sendMessage,
    markAsRead,
    isDrawerOpen,
    setDrawerOpen,
    activeConversation,
    setActiveConversation,
    sendTypingIndicator,
    sendTestMessage,
  }), [conversations, totalUnreadCount, sendMessage, markAsRead, isDrawerOpen, activeConversation, sendTypingIndicator, sendTestMessage])

  return (
    <QuickChatContext.Provider value={value}>
      {children}
    </QuickChatContext.Provider>
  )
}

export const useQuickChat = () => {
  const ctx = useContext(QuickChatContext)
  if (!ctx) throw new Error('useQuickChat must be used within QuickChatProvider')
  return ctx
}
